/**
 * PreparedContent - the loader's source-phase product: every content read a loader makes for the
 * AppSites it prepared.
 *
 * Mirrors csharp Assembler/Config/PreparedContent.cs.
 *
 * Responsibilities:
 * - Answer the four loader content reads (root json, template html, template data json, folder data json)
 * - Keep IConfig immutable: a source never copies or mutates itself to serve prepared content
 *
 * Current behavior:
 * - Read mode (fromFiles): a filesystem source read the text of every file here. Keys are logical
 *   (AppSite, file name), never filesystem paths. A null value means the source confirmed the file does
 *   not exist. JSON is parsed from text at the read site, so parse cost stays in the phase that reads,
 *   and invalid JSON raises with its key.
 * - Resident mode (resident): the source already holds its content in memory, so each read is answered
 *   by the source itself (its serve counters still count per read).
 * - Produced only by IConfig.prepare; template-derived only, never request-derived, and never retained
 *   beyond one load.
 */

import { JsonObject } from '../app/json/jsonValue.js';
import { JsonUtility } from '../app/jsonUtility.js';
import type { IConfig } from './iConfig.js';

type SiteFiles = ReadonlyMap<string, ReadonlyMap<string, string | null>>;

const NO_SITE_FILES: SiteFiles = new Map();
const NO_ROOT_FILES: ReadonlyMap<string, string | null> = new Map();

export class PreparedContent {
    private readonly _source: IConfig | null;
    private readonly _appSites: ReadonlySet<string>;
    private readonly _html: SiteFiles;
    private readonly _data: SiteFiles;
    private readonly _folder: SiteFiles;
    private readonly _root: ReadonlyMap<string, string | null>;

    private constructor(
        source: IConfig | null,
        appSites: ReadonlySet<string>,
        html: SiteFiles,
        data: SiteFiles,
        folder: SiteFiles,
        root: ReadonlyMap<string, string | null>,
    ) {
        this._source = source;
        this._appSites = appSites;
        this._html = html;
        this._data = data;
        this._folder = folder;
        this._root = root;
    }

    /**
     * Read mode: the text a filesystem source read for appSites. The per-AppSite maps
     * (AppSite -> file name -> text) cover the source's complete resolvable set for each prepared
     * AppSite, so a file name they do not record does not exist in the source. root is the closed set
     * of root-level files the loader reads.
     * Keys: appSites holds lower-cased AppSite names (case-insensitive); html/data file names are
     * lower-cased (case-insensitive, as the source's per-AppSite metadata); AppSite keys of the maps,
     * folder file names and root file names are exact.
     */
    static fromFiles(
        appSites: ReadonlySet<string>,
        html: SiteFiles,
        data: SiteFiles,
        folder: SiteFiles,
        root: ReadonlyMap<string, string | null>,
    ): PreparedContent {
        return new PreparedContent(null, appSites, html, data, folder, root);
    }

    /**
     * Resident mode: the source's content is already in memory, so reads are answered by the source.
     */
    static resident(source: IConfig): PreparedContent {
        return new PreparedContent(source, new Set<string>(), NO_SITE_FILES, NO_SITE_FILES, NO_SITE_FILES, NO_ROOT_FILES);
    }

    /** Root-level JSON file (e.g. dynamicappsite.json). Empty object when the file does not exist. */
    getRootJson(fileName: string): JsonObject {
        if (this._source !== null) {
            return this._source.getRootJson(fileName);
        }
        if (!this._root.has(fileName)) {
            throw new Error('content was not prepared: ' + fileName);
        }
        const text = this._root.get(fileName) ?? null;
        return text === null ? new JsonObject() : PreparedContent.parse(text, fileName);
    }

    /** Template HTML. null when the template has no HTML file. */
    getHtmlContent(appSite: string, fileName: string): string | null {
        if (this._source !== null) {
            return this._source.getHtmlContent(appSite, fileName);
        }
        return this.lookup(this._html, appSite, fileName.toLowerCase(), fileName);
    }

    /** Template data JSON. null when the template has no JSON file. */
    getDataJson(appSite: string, fileName: string): JsonObject | null {
        if (this._source !== null) {
            return this._source.getDataJson(appSite, fileName);
        }
        const text = this.lookup(this._data, appSite, fileName.toLowerCase(), fileName);
        return text === null ? null : PreparedContent.parse(text, appSite + '/' + fileName);
    }

    /** Folder-level JSON shared by every AppSite of the prepared AppSite's folder. null when absent. */
    getFolderDataJson(appSite: string, fileName: string): JsonObject | null {
        if (this._source !== null) {
            return this._source.getFolderDataJson(appSite, fileName);
        }
        const text = this.lookup(this._folder, appSite, fileName, fileName);
        return text === null ? null : PreparedContent.parse(text, appSite + '/' + fileName);
    }

    private lookup(files: SiteFiles, appSite: string, key: string, fileName: string): string | null {
        if (!this._appSites.has(appSite.toLowerCase())) {
            throw new Error('content was not prepared: ' + appSite + '/' + fileName);
        }
        return files.get(appSite)?.get(key) ?? null;
    }

    private static parse(text: string, key: string): JsonObject {
        try {
            return JsonUtility.parseJsonStringStrict(text);
        } catch (ex: unknown) {
            const message = ex instanceof Error ? ex.message : String(ex);
            throw new Error('invalid JSON in prepared content ' + key + ': ' + message, { cause: ex });
        }
    }
}
